import {QueryCacheKeys} from '../api/cache';
import {getTags} from '../api/tags';
import {getTransactions} from '../api/transactions';
import {useQuery} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import type {Transaction, TransactionFilters} from 'shared/src/models';

type TransactionQuery = TransactionFilters & {
	filter?: (transaction: Transaction) => boolean;
	skip?: boolean;
};

interface TransactionsResult {
	transactions: Transaction[] | null;
	refetch: () => void;
}

export const useTransactions = (query: TransactionQuery): TransactionsResult => {
	const {data: rawTransactions, refetch} = useQuery({
		queryKey: QueryCacheKeys.transactions(query),
		queryFn: () => getTransactions(query),
	});

	let transactions = rawTransactions ?? null;
	if (transactions && query.filter) {
		transactions = transactions.filter(query.filter);
	}

	return {
		transactions,
		refetch,
	};
};

interface GenericQuery {
	startDate: DateTime;
	endDate: DateTime;
}

export const useIncomeTransactions = ({startDate, endDate}: GenericQuery): TransactionsResult => {
	const {data: tags} = useQuery({
		queryKey: QueryCacheKeys.tags(),
		queryFn: getTags,
	});

	const excludedIncomeTag = tags?.find((tag) => tag.internalId === 'excludedIncome');

	return useTransactions({
		startDate,
		endDate,
		excludedTagIds: [excludedIncomeTag?.id].filter((id) => id !== undefined),
		filter: (transaction: Transaction) => {
			return transaction.amount > 0;
		},
		skip: !excludedIncomeTag,
	});
};

export const useExpenseTransactions = ({startDate, endDate}: GenericQuery): TransactionsResult => {
	const {data: tags} = useQuery({
		queryKey: QueryCacheKeys.tags(),
		queryFn: getTags,
	});

	const excludedExpensesTag = tags?.find((tag) => tag.internalId === 'excludedExpenses');

	return useTransactions({
		startDate,
		endDate,
		excludedTagIds: [excludedExpensesTag?.id].filter((id) => id !== undefined),
		filter: (transaction: Transaction) => {
			return transaction.amount < 0;
		},
		skip: !excludedExpensesTag,
	});
};
